import React, { useState } from "react";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { Button, useToast } from "../../ui";

/**
 * Staff-only Mark-inactive / Reactivate control for an Auto/Home/Umbrella
 * policy (Session I T4 pin, 2026-07-12). Deliberately component-level and
 * self-contained: the next-phase "Data Custodian" data-fix queue reuses this
 * exact affordance as a one-click apply action.
 *
 * INACTIVATE = PUT /api/{lob}/delete/:id — the legacy softDelete path (sets
 * policyActive:false); the label is ALWAYS "Mark inactive", never "Delete".
 * REACTIVATE = PUT /api/{lob}/reactivate/:id. Both return the UPDATED policy
 * ({new:true}), which we hand straight to onChanged so the caller reflects the
 * new state (and the drawer offers the inverse — Reactivate is the undo for a
 * mis-click). OtherPolicy has no route, so this renders only for auto/home/
 * umbrella. Gated behind FEATURES.policyInactivate; the backend is the real
 * boundary (referral 403s at the route).
 */

const INACTIVATE = {
  auto: (id) => API.softDeleteAuto(id),
  home: (id) => API.softDeleteProperty(id),
  umbrella: (id) => API.softDeleteUmbrella(id)
};
const REACTIVATE = {
  auto: (id) => API.reactivateAuto(id),
  home: (id) => API.reactivateProperty(id),
  umbrella: (id) => API.reactivateUmbrella(id)
};

function PolicyStatusAction({ policy, onChanged, inactiveLabel = "Mark inactive", variant }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  if (!FEATURES.policyInactivate || !policy) {
    return null;
  }
  const inactive = policy.policyActive === false;
  const call = (inactive ? REACTIVATE : INACTIVATE)[policy.lobKey];
  if (!call) {
    return null; // no inactivate/reactivate route for this line (e.g. otherpolicy)
  }

  const run = async () => {
    setSaving(true);
    try {
      const res = await call(policy._id);
      // Server returns the updated policy ({new:true}); fall back to a local
      // flip so the UI still reflects the change if the body is empty. The
      // toggle flips the state: the NEW active status is the opposite of the
      // current one — i.e. active iff it was inactive.
      const updated =
        res && res.data && res.data._id
          ? res.data
          : { ...policy, policyActive: policy.policyActive === false };
      toast.success(inactive ? "Policy reactivated." : "Policy marked inactive.");
      onChanged(updated);
    } catch (err) {
      toast.error("That didn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Button variant={variant || (inactive ? "secondary" : "ghost")} loading={saving} onClick={run}>
      {inactive ? "Reactivate" : inactiveLabel}
    </Button>
  );
}

export default PolicyStatusAction;
